<template>
  <article class="content-card" :class="viewMode === 'list' ? 'is-list' : 'is-grid'">
    <button class="card-hit" type="button" :aria-label="`查看《${displayTitle}》详情`" @click="goToDetail(book.id)">
      <span class="cover" :class="[`cover-${tintIndex}`, `media-${String(book.mediaType || 'TEXT').toLowerCase()}`]">
        <protected-cover v-if="book.coverImage && !coverBroken" :src="book.coverImage" :alt="''" :fit="book.mediaType === 'AUDIO' ? 'contain' : 'cover'" @error="coverBroken = true" />
        <span v-else class="cover-fallback">
          <span class="cover-media">{{ mediaLabel }}</span>
          <span class="cover-title">{{ displayTitle }}</span>
          <span class="cover-rule"></span>
          <span class="cover-author">{{ book.author || '佚名' }}</span>
        </span>
        <span v-if="showVisibility && book.visibility" class="cover-badge">{{ book.visibility === 'PUBLIC' ? '公开' : '私有' }}</span>
      </span>
      <span class="card-info">
        <span class="card-title">{{ displayTitle }}</span>
        <span class="card-author">{{ book.author || '佚名' }}</span>
        <span class="card-summary">
          <span>{{ book.category || mediaLabel }}</span>
          <span v-if="hasProgress" class="progress-label">{{ progressLabel }}</span>
          <span v-else class="progress-label">未开始</span>
        </span>
        <span v-if="hasProgress && progressPercent < 100" class="progress-track" aria-hidden="true"><i :style="{ width: progressPercent + '%' }"></i></span>
      </span>
    </button>

    <div v-if="canEdit(book) || canRemove(book)" class="card-actions">
      <button class="more-button" type="button" :aria-label="`更多操作：${displayTitle}`" :aria-expanded="menuOpen ? 'true' : 'false'" aria-haspopup="menu" @click.stop="menuOpen = !menuOpen"><nricon name="more" :size="19" /></button>
      <template v-if="menuOpen">
        <button class="menu-backdrop" type="button" aria-label="关闭操作菜单" @click.stop="menuOpen = false"></button>
        <div class="action-menu" role="menu" :aria-label="`${displayTitle} 的操作`">
          <button v-if="canEdit(book)" type="button" role="menuitem" @click.stop="editItem"><nricon name="edit" /> 编辑信息</button>
          <button v-if="canRemove(book)" type="button" role="menuitem" @click.stop="removeItem"><nricon name="collection" /> 移出书库</button>
          <button v-if="canEdit(book)" class="danger" type="button" role="menuitem" @click.stop="deleteItem"><nricon name="delete" /> 删除内容</button>
        </div>
      </template>
    </div>
  </article>
</template>

<script>
import { cleanTitle } from '@/utils/title'
import ProtectedCover from '@/components/common/ProtectedCover.vue'

export default {
  name: 'ContentCard',
  components: { ProtectedCover },
  props: {
    book: { type: Object, required: true },
    viewMode: { type: String, default: 'grid' },
    goToDetail: { type: Function, required: true },
    canEdit: { type: Function, required: true },
    handleEdit: { type: Function, required: true },
    handleDelete: { type: Function, required: true },
    showVisibility: { type: Boolean, default: false },
    canRemove: { type: Function, default: () => false },
    handleRemove: { type: Function, default: () => {} }
  },
  data() { return { menuOpen: false, coverBroken: false } },
  computed: {
    displayTitle() { return cleanTitle(this.book.title) || '未命名' },
    hasProgress() {
      const progress = this.book.readingProgress || {}
      return !!(progress.chapterId || progress.pageIndex != null || progress.trackId || progress.positionSeconds > 0)
    },
    mediaLabel() {
      return ({ PDF: 'PDF', AUDIO: '有声书' })[this.book.mediaType] || '电子书'
    },
    progressPercent() {
      const progress = this.book.readingProgress || {}
      let value = progress.percentage
      if (progress.mediaType === 'PDF' && progress.totalPages) value = ((Number(progress.pageIndex || 0) + 1) / progress.totalPages)
      if (progress.mediaType === 'AUDIO' && progress.totalDurationSeconds) value = Number(progress.overallPositionSeconds || 0) / progress.totalDurationSeconds
      return Math.max(0, Math.min(100, Math.round((Number(value) || 0) * 100)))
    },
    progressLabel() {
      if (this.progressPercent >= 100) return '已完成'
      const progress = this.book.readingProgress || {}
      if (progress.mediaType === 'PDF') return `第 ${Number(progress.pageIndex || 0) + 1} 页`
      if (progress.mediaType === 'AUDIO') return `第 ${Number(progress.trackIndex || 0) + 1} / ${progress.totalTracks || '—'} 轨 · ${this.formatDuration(progress.positionSeconds)}`
      return this.progressPercent ? `${this.progressPercent}% 已读` : '进行中'
    },
    tintIndex() {
      let hash = 0
      const text = this.displayTitle || String(this.book.id || '')
      for (let i = 0; i < text.length; i++) hash = (hash * 31 + text.charCodeAt(i)) | 0
      return Math.abs(hash) % 4
    }
  },
  watch: {
    'book.id'() { this.coverBroken = false; this.menuOpen = false }
  },
  methods: {
    formatDuration(value) {
      const seconds = Math.max(0, Math.floor(Number(value) || 0))
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      return hours ? `${hours} 小时 ${minutes} 分` : `${minutes} 分钟`
    },
    editItem() { this.menuOpen = false; this.handleEdit(this.book) },
    deleteItem() { this.menuOpen = false; this.handleDelete(this.book) },
    removeItem() { this.menuOpen = false; this.handleRemove(this.book) }
  }
}
</script>

<style lang="scss" scoped>
.content-card { position: relative; min-width: 0; }
.card-hit { display: block; width: 100%; padding: 0; color: inherit; border: 0; background: transparent; text-align: left; cursor: pointer; }
.cover { position: relative; display: block; width: 100%; overflow: hidden; aspect-ratio: 5 / 7; border: 1px solid var(--nr-border); border-radius: 5px; background: var(--nr-surface-2); box-shadow: var(--nr-shadow); transition: box-shadow 180ms ease, transform 180ms ease;
  img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
}
.cover.media-audio { background: var(--nr-surface-2); }
.cover.media-audio img { object-fit: contain; }
.content-card:hover .cover { box-shadow: var(--nr-shadow-hover); transform: translateY(-2px); }
.cover-fallback { position: absolute; inset: 0; display: flex; flex-direction: column; padding: 17px 12px 13px; overflow: hidden; color: #fff; text-align: left;
  &::after { position: absolute; inset: 0; background: linear-gradient(180deg, transparent 48%, rgba(0,0,0,.25)); content: ''; pointer-events: none; }
}
.cover-title { z-index: 1; display: -webkit-box; overflow: hidden; margin-top: 10px; font-family: var(--nr-font-display); font-size: clamp(14px, 1.35vw, 19px); font-weight: 650; line-height: 1.38; -webkit-box-orient: vertical; -webkit-line-clamp: 4; }
.cover-media { z-index: 1; align-self: flex-start; padding: 3px 6px; border: 1px solid rgba(255,255,255,.45); border-radius: 99px; font-size: 9px; line-height: 1.2; }
.cover-rule { z-index: 1; width: 22px; height: 2px; margin-top: auto; margin-bottom: 8px; background: rgba(255,255,255,.8); }
.cover-author { z-index: 1; overflow: hidden; color: rgba(255,255,255,.86); font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
.cover-0 .cover-fallback { background: linear-gradient(145deg, #557462, #293d34); }
.cover-1 .cover-fallback { background: linear-gradient(145deg, #65798a, #36434c); }
.cover-2 .cover-fallback { background: linear-gradient(145deg, #a17456, #624536); }
.cover-3 .cover-fallback { background: linear-gradient(145deg, #766d86, #454153); }
.cover-badge { position: absolute; top: 8px; left: 8px; z-index: 2; padding: 3px 7px; color: #fff; border-radius: 99px; background: rgba(20,31,24,.58); font-size: 10px; backdrop-filter: blur(6px); }
.card-info { display: block; min-width: 0; padding: 10px 2px 0; }
.card-title { display: -webkit-box; overflow: hidden; color: var(--nr-text); font-size: 15px; font-weight: 600; line-height: 1.45; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.card-author { display: block; overflow: hidden; margin-top: 3px; color: var(--nr-text-2); font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.card-summary { display: flex; justify-content: space-between; gap: 6px; margin-top: 7px; color: var(--nr-text-3); font-size: 10px; }
.card-summary > span:first-child { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.progress-label { flex-shrink: 0; }
.progress-track { display: block; height: 3px; overflow: hidden; margin-top: 6px; border-radius: 8px; background: var(--nr-surface-3); i { display: block; height: 100%; border-radius: inherit; background: var(--nr-accent); } }
.card-actions { position: absolute; z-index: 15; top: 7px; right: 7px; }
.more-button { display: grid; width: 36px; height: 36px; place-items: center; color: #fff; border: 1px solid rgba(255,255,255,.35); border-radius: 50%; background: rgba(23,32,27,.7); box-shadow: 0 3px 10px rgba(0,0,0,.15); cursor: pointer; backdrop-filter: blur(7px); &:hover { background: var(--nr-accent); } }
.menu-backdrop { position: fixed; z-index: 0; inset: 0; width: 100vw; height: 100vh; border: 0; background: transparent; cursor: default; }
.action-menu { position: absolute; z-index: 2; top: 42px; right: 0; min-width: 145px; padding: 5px; border: 1px solid var(--nr-border); border-radius: 10px; background: var(--nr-surface); box-shadow: var(--nr-shadow-float);
  button { display: flex; width: 100%; min-height: 40px; align-items: center; gap: 8px; padding: 0 10px; color: var(--nr-text); border: 0; border-radius: 7px; background: transparent; font-size: 12px; text-align: left; cursor: pointer; &:hover { background: var(--nr-surface-2); } &.danger { color: var(--nr-danger); } }
}
.is-list { display: flex; align-items: center; padding: 9px 12px; border: 1px solid var(--nr-border); border-radius: 12px; background: var(--nr-surface); transition: background 160ms ease; &:hover { background: color-mix(in srgb, var(--nr-surface) 82%, var(--nr-accent-bg)); }
  .card-hit { display: flex; align-items: center; gap: 16px; }
  .cover { width: 58px; flex: 0 0 58px; aspect-ratio: 5 / 7; }
  .cover-fallback { padding: 7px 6px; }
  .cover-media, .cover-rule, .cover-author { display: none; }
  .cover-title { margin-top: 0; font-size: 10px; -webkit-line-clamp: 4; }
  .card-info { flex: 1; padding: 0 54px 0 0; }
  .card-title { font-size: 15px; }
  .card-summary { justify-content: flex-start; gap: 14px; }
  .progress-track { max-width: 340px; }
  .card-actions { top: 50%; right: 13px; transform: translateY(-50%); }
}
@media (max-width: 767px) {
  .card-title { font-size: 14px; }
  .cover-fallback { padding: 13px 10px 10px; }
  .cover-title { font-size: 14px; }
  .more-button { width: 34px; height: 34px; }
  .is-list .cover { width: 52px; flex-basis: 52px; }
}
</style>
